import React, { Component } from 'react';
import {
  Card,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Typography,
  List,
  ListItem,
  IconButton,
  Button,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClock } from '@fortawesome/pro-light-svg-icons';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons';
import { getPlaceStatus } from '../../utils/placeStatusUtils';
import _ from 'lodash';
import { PlaceStatus } from './constants';
import PortalTooltip from './PortalTooltip';
import PortalLink from './PortalLink';

class PlaceCard extends Component {
  getStatusDetails = (placeStatus) => {
    const { status, closesAt, opensTomorrowAt } = placeStatus;
    const build = (label, opts = {}) => ({
      label,
      cssClass: label ? _.camelCase(label) : 'hoursUnknown',
      ariaLabel: opts.ariaLabel || label,
      subtext: opts.subtext || null,
    });

    switch (status) {
      case PlaceStatus.OPEN:
        return build('Open', {
          ariaLabel: 'Currently Open',
          subtext: closesAt ? `Closes at ${closesAt}` : null,
        });
      case PlaceStatus.CLOSING_SOON:
        return build('Closing Soon', {
          subtext: closesAt ? `Closes at ${closesAt}` : null,
        });
      case PlaceStatus.CLOSED:
        return build('Closed', {
          ariaLabel: 'Currently Closed',
          subtext: opensTomorrowAt ? `Opens tomorrow at ${opensTomorrowAt}` : null,
        });
      default:
        return build(null);
    }
  };

  render() {
    const { location } = this.props.data;
    const isSelected = this.props.selectedLocation?.id === location.id;
    const hasHours = Array.isArray(location.hours) && location.hours.length > 0;
    const placeStatus = getPlaceStatus(location.hours);
    const statusDetails = this.getStatusDetails(placeStatus);

    const cardAriaLabel = statusDetails.ariaLabel
      ? `${location.title}. ${statusDetails.ariaLabel}. Select card to view location on map.`
      : `${location.title}. Select card to view location on map.`;

    return (
      <Card
        className={`myOdu__placeCard${isSelected ? ' myOdu__placeCard--selected' : ''}`}
        id={this.props.id}
        elevation={0}
        onClick={(e) => {
          if (e.target.closest('a, button')) return;
          this.props.onSelectLocation(location);
        }}
        sx={{ cursor: 'pointer' }}
      >
        <Box id={this.props.id + '__cardContent'} sx={{ px: 1, py: 0.5 }}>
          {/* Row 1: Name + Operating Status */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ lineHeight: 1, mb: 0 }}
          >
            {location.url ? (
              <Typography
                component="h4"
                sx={{ fontWeight: 600, fontSize: '0.85rem', lineHeight: 1, m: 0 }}
                id={this.props.id + '__h4_title'}
              >
                {' '}
                <Typography id={this.props.id + '__text_title'} className="sr-only">
                  {location.title}
                </Typography>
                <PortalLink
                  sx={{ fontSize: 'inherit', lineHeight: 'inherit' }}
                  href={location.url}
                  underline="hover"
                  target="_blank"
                  id={this.props.id + '__link_title'}
                  aria-label={`Visit ${location.title} website`}
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                >
                  {location.title}
                </PortalLink>
                <Button
                  className="sr-only-focusable"
                  id={this.props.id + '__button_showOnMap'}
                  onClick={(e) => {
                    e.stopPropagation();
                    this.props.onSelectLocation(location);
                  }}
                  aria-label={cardAriaLabel}
                >
                  Show on map
                </Button>
              </Typography>
            ) : (
              <Typography
                component="button"
                sx={{
                  fontWeight: 600,
                  fontSize: '0.85rem',
                  lineHeight: 1,
                  m: 0,
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                  textAlign: 'left',
                  color: 'inherit',
                  font: 'inherit',
                }}
                id={this.props.id + '__button_title'}
                onClick={(e) => {
                  e.stopPropagation();
                  this.props.onSelectLocation(location);
                }}
                aria-label={cardAriaLabel}
              >
                {location.title}
              </Typography>
            )}
            {statusDetails.label && (
              <Typography
                className={'myOdu__operatingStatus ' + statusDetails.cssClass}
                variant="smallest"
                id={this.props.id + '__text_status'}
              >
                {statusDetails.label}
              </Typography>
            )}
          </Stack>

          {/* Row 2: Closes at X / Opens tomorrow at X */}
          {statusDetails.subtext && (
            <Typography
              variant="smallest"
              sx={{ lineHeight: 1, mt: 0 }}
              id={this.props.id + '__text_subtext'}
            >
              {statusDetails.subtext}
            </Typography>
          )}

          {/* Row 3: Address + Clock/Chevron for hours */}
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            {location.address && (
              <Typography
                variant="smallest"
                sx={{ fontStyle: 'italic' }}
                id={this.props.id + '__text_address'}
              >
                {location.address}
              </Typography>
            )}
            {hasHours && (
              <PortalTooltip
                title={this.state?.hoursOpen ? 'Hide Operating Hours' : 'Show Operating Hours'}
              >
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    this.setState((prev) => ({ hoursOpen: !prev?.hoursOpen }));
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      e.stopPropagation();
                      this.setState((prev) => ({ hoursOpen: !prev?.hoursOpen }));
                    }
                  }}
                  id={this.props.id + '__iconButton_hours'}
                  aria-label={`Operating hours for ${location.title}`}
                  aria-expanded={!!this.state?.hoursOpen}
                  aria-controls={this.props.id + '__accordionDetails_hours'}
                  sx={{ ml: 1 }}
                >
                  <FontAwesomeIcon icon={faClock} size="sm" />
                  <FontAwesomeIcon
                    icon={faChevronDown}
                    size="xs"
                    style={{ marginLeft: '0.25rem' }}
                  />
                </IconButton>
              </PortalTooltip>
            )}
          </Stack>
        </Box>

        {/* Hours accordion */}
        {hasHours && (
          <Accordion
            expanded={!!this.state?.hoursOpen}
            onChange={(e, expanded) => this.setState({ hoursOpen: expanded })}
            elevation={0}
            sx={{
              '&:before': { display: 'none' },
              boxShadow: 'none',
              m: '0 !important',
            }}
            className="myOdu__placeCard_hoursAccordion"
          >
            <AccordionSummary sx={{ display: 'none' }} />
            <AccordionDetails
              className="hoursDetails"
              sx={{ p: 0 }}
              id={this.props.id + '__accordionDetails_hours'}
            >
              <Typography component="h5" variant="smallest" sx={{ pl: 1, fontWeight: 600 }}>
                Operating Hours
              </Typography>
              <List className="operatingHours" id={this.props.id + '__list_hours'} sx={{ py: 0 }}>
                {location.hours.map((item, index) => (
                  <ListItem
                    key={this.props.id + '__listItem_hours_' + index}
                    id={this.props.id + '__listItem_hours_' + index}
                  >
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      spacing={2}
                      sx={{ width: '100%', px: 1 }}
                    >
                      <Typography variant="smallest">
                        {item.title}
                        {item.date ? ` ${item.date}` : ''}
                        {item.isOnline && (
                          <Typography
                            component="span"
                            sx={{ fontStyle: 'italic' }}
                            variant="smallest"
                          >
                            {' '}
                            (Online){' '}
                          </Typography>
                        )}
                      </Typography>
                      <Typography variant="smallest">{item.times}</Typography>
                    </Stack>
                  </ListItem>
                ))}
              </List>
            </AccordionDetails>
          </Accordion>
        )}
      </Card>
    );
  }
}

export default PlaceCard;
